import React, { useState } from "react";
import { apiUrl } from "../../config/api";
import "./styles/affiliate-generator.css";

const AffiliateContentGenerator = ({
  isOpen,
  onClose,
  mode,
  affiliateCode,
  userName,
}) => {
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Form state
  const [tone, setTone] = useState("professional");
  const [audience, setAudience] = useState("business_owners");

  const modeConfig = {
    email: {
      title: "📧 Email Generator",
      description: "Generate personalized email copy with your affiliate link",
      tones: ["professional", "casual", "excited", "educational"],
      audiences: [
        "business_owners",
        "agency_owners",
        "coaches",
        "founders",
        "marketers",
        "solopreneurs",
      ],
    },
    social: {
      title: "📱 Social Media Post Generator",
      description: "Create engaging social posts with your affiliate link",
      tones: ["casual", "excited", "inspirational", "educational"],
      audiences: [
        "entrepreneurs",
        "business_owners",
        "digital_marketers",
        "agency_owners",
        "coaches",
        "content_creators",
      ],
    },
    ads: {
      title: "🎯 Ad Copy Generator",
      description: "Craft high-converting ad copy with your affiliate link",
      tones: ["persuasive", "urgent", "benefit-focused", "problem-solution"],
      audiences: [
        "small_business_owners",
        "agency_owners",
        "entrepreneurs",
        "coaches",
        "consultants",
        "saas_founders",
      ],
    },
  };

  const config = modeConfig[mode] || modeConfig.email;

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    setGeneratedContent("");

    try {
      const response = await fetch(apiUrl("/api/affiliate/generate-content"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mode,
          tone,
          audience,
          affiliateCode,
          userName: userName || "there",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to generate content");
      }

      const data = await response.json();
      setGeneratedContent(data.content);
    } catch (err) {
      setError(err.message || "Failed to generate content");
      console.error("Generation error:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([generatedContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${mode}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="generator-overlay" onClick={onClose}>
      <div className="generator-modal" onClick={(e) => e.stopPropagation()}>
        <div className="generator-header">
          <div>
            <h2>{config.title}</h2>
            <p className="generator-subtitle">{config.description}</p>
          </div>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="generator-body">
          {!generatedContent ? (
            <>
              <div className="form-group">
                <label>Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  disabled={generating}
                >
                  {config.tones.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1).replace("-", " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Target Audience</label>
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  disabled={generating}
                >
                  {config.audiences.map((a) => (
                    <option key={a} value={a}>
                      {a
                        .split("_")
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(" ")}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="error-message">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                className="btn-primary btn-generate"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <span className="spinner"></span>
                    Generating with Optimus AI...
                  </>
                ) : (
                  <>
                    <span>✨</span>
                    Generate{" "}
                    {mode === "email"
                      ? "Email"
                      : mode === "social"
                        ? "Post"
                        : "Ad Copy"}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="generated-content">
                <pre>{generatedContent}</pre>
              </div>

              <div className="generator-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setGeneratedContent("")}
                >
                  <span>🔄</span>
                  Generate New
                </button>
                <button className="btn-primary" onClick={handleCopy}>
                  <span>{copied ? "✓" : "📋"}</span>
                  {copied ? "Copied!" : "Copy to Clipboard"}
                </button>
                <button className="btn-primary" onClick={handleDownload}>
                  <span>⬇️</span>
                  Download
                </button>
              </div>

              <div className="generator-tip">
                💡 <strong>Tip:</strong> Copy this and use it in Optimus chat to
                send via your connected CRMs, post to social media, or create ad
                campaigns!
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AffiliateContentGenerator;
